import React, { useState, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '@/components/ui/Button';
import { ArrowRight, ArrowLeft, CheckCircle, BarChart2, X } from 'lucide-react';

const TeamMaturityChart = React.lazy(() => import('@/components/charts/TeamMaturityChart'));

const dimensions = [
  {
    id: 'enneagram',
    title: 'Enneagram Integration',
    question: 'How well does the team understand and utilize diverse personality types and motivations?',
    benchmark: 4.2,
    teamAverage: 3.6,
    definition: 'The ability to recognize, appreciate, and leverage the diverse personality types and core motivations within the team for enhanced collaboration.',
    insight: 'Understanding diverse personality types helps the team mitigate conflict and communicate more effectively, reducing friction during high-stress periods.',
    actions: ['Conduct a team Enneagram typing session.', 'Create "working with me" guides based on type.', 'Practice type-specific conflict resolution strategies.']
  },
  {
    id: 'personal_mastery',
    title: 'Personal Mastery',
    question: 'How effectively do team members demonstrate self-awareness and continuous self-improvement?',
    benchmark: 3.8,
    teamAverage: 3.2,
    definition: 'The ongoing commitment of individuals to self-awareness, emotional intelligence, and continuous personal and professional development.',
    insight: 'Focusing on self-awareness and continuous improvement leads to stronger individual contributions and a culture of continuous learning and adaptability.',
    actions: ['Implement regular self-reflection check-ins.', 'Establish individual growth plans tied to team goals.', 'Provide coaching on emotional intelligence and active listening.']
  },
  {
    id: 'emotional_resilience',
    title: 'Emotional Resilience',
    question: 'How capably does the team manage stress, recover from setbacks, and maintain composure?',
    benchmark: 4.0,
    teamAverage: 4.1,
    definition: 'The collective capacity of the team to manage stress, adapt to change, and recover quickly from setbacks while maintaining composure.',
    insight: 'High emotional resilience allows the team to bounce back from setbacks quickly and maintain high morale, even when facing significant operational challenges.',
    actions: ['Conduct post-mortem reviews without assigning blame.', 'Practice stress-reduction techniques as a group.', 'Intentionally celebrate small wins during long, demanding projects.']
  },
  {
    id: 'social_drives',
    title: 'Social Drives',
    question: 'How aligned are the team\'s intrinsic motivations with collaborative and organizational goals?',
    benchmark: 3.5,
    teamAverage: 2.9,
    definition: 'The alignment of the team\'s intrinsic motivations—such as the need for connection, status, and safety—with their collaborative efforts and shared goals.',
    insight: 'Aligning intrinsic motivations with team goals fosters deeper engagement and commitment, ensuring everyone feels valued and connected to a larger purpose.',
    actions: ['Map individual social drives to project roles and responsibilities.', 'Create diverse reward mechanisms that cater to different motivations.', 'Regularly align personal developmental goals with overarching team objectives.']
  },
  {
    id: 'energy_centres',
    title: 'Energy Centres',
    question: 'How well does the team balance thinking, feeling, and doing (action) in their work approach?',
    benchmark: 3.9,
    teamAverage: 3.8,
    definition: 'The team\'s effectiveness in balancing cognitive (thinking), emotional (feeling), and somatic (doing) energies to approach tasks holistically.',
    insight: 'Balancing cognitive (thinking), emotional (feeling), and somatic (doing) approaches ensures holistic problem solving and prevents collective burnout.',
    actions: ['Rotate meeting facilitation styles to engage different energy centers.', 'Ensure major decisions formally consider logic, impact on people, and execution feasibility.', 'Schedule regular physical movement breaks during long collaboration sessions.']
  }
];

const options = [
  { value: 1, label: 'Rarely / Strongly Disagree' },
  { value: 2, label: 'Infrequently / Disagree' },
  { value: 3, label: 'Sometimes / Neutral' },
  { value: 4, label: 'Frequently / Agree' },
  { value: 5, label: 'Almost Always / Strongly Agree' }
];



export function TeamMaturityAssessmentPage() {
  const [hasStarted, setHasStarted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [showResults, setShowResults] = useState(false);
  const [viewMode, setViewMode] = useState<'individual' | 'team'>('individual');
  const [showBenchmarks, setShowBenchmarks] = useState(true);
  const [gridDensity, setGridDensity] = useState<'low' | 'medium' | 'high'>('medium');
  const [chartType, setChartType] = useState<'radar' | 'trend'>('radar');
  const [selectedDimension, setSelectedDimension] = useState<any>(null);

  const handleSelect = (value: number) => {
    setAnswers(prev => ({
      ...prev,
      [dimensions[currentStep].id]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < dimensions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const resetAssessment = () => {
    setHasStarted(false);
    setCurrentStep(0);
    setAnswers({});
    setShowResults(false);
  };

  const calculateResults = () => {
    return dimensions.map(d => ({
      subject: d.title,
      individual: answers[d.id] || 0,
      teamAverage: d.teamAverage,
      benchmark: d.benchmark,
      fullMark: 5,
    }));
  };

  const generateTrendData = () => {
    const p1: any = { period: '6 Mos Ago' };
    const p2: any = { period: '3 Mos Ago' };
    const current: any = { period: 'Current' };
    
    dimensions.forEach(d => {
      const base = d.teamAverage || 3;
      p1[d.title] = Math.max(1, base - 0.8);
      p2[d.title] = Math.max(1, base - 0.3);
      current[d.title] = viewMode === 'individual' ? (answers[d.id] || 0) : base;
    });
    
    return [p1, p2, current];
  };

  const getHeatmapColor = (gap: number) => {
    if (gap >= 1) return 'bg-emerald-500 text-white shadow-sm';
    if (gap >= 0.3) return 'bg-emerald-200 text-emerald-900 shadow-sm';
    if (gap > -0.3) return 'bg-ground-deep/50 text-ink/70';
    if (gap > -1) return 'bg-rose-200 text-rose-900 shadow-sm';
    return 'bg-rose-500 text-white shadow-sm';
  };

  const generateHeatmapData = () => {
    const teamMates = ['You', 'Member A', 'Member B', 'Member C', 'Member D'];
    return teamMates.map((member, index) => {
      const isYou = member === 'You';
      const row: any = { member };
      dimensions.forEach((d, dIndex) => {
        if (isYou) {
          row[d.id] = (answers[d.id] || 0) - d.teamAverage;
        } else {
          // Deterministic pseudo-random variation based on member and dimension
          const pseudoRandom = Math.sin((dIndex + 1) * (index + 1)) * 1.5;
          row[d.id] = pseudoRandom;
        }
      });
      return row;
    });
  };

  const isCurrentAnswered = answers[dimensions[currentStep]?.id] !== undefined;

  return (
    <>
      <Helmet>
        <title>Team Maturity Assessment | Free Organisational Diagnostic | FIVE LENS</title>
        <meta name="description" content="Assess your team's behavioural maturity across five psychological dimensions. Data-driven insights for targeted leadership and team development." />
        <meta property="og:title" content="Team Maturity Assessment | Free Organisational Diagnostic | FIVE LENS" />
        <meta property="og:description" content="Assess your team's behavioural maturity across five psychological dimensions. Data-driven insights for targeted leadership and team development." />
        <meta name="twitter:title" content="Team Maturity Assessment | Free Organisational Diagnostic | FIVE LENS" />
        <meta name="twitter:description" content="Assess your team's behavioural maturity across five psychological dimensions. Data-driven insights for targeted leadership and team development." />
      </Helmet>

      <main className="pt-32 pb-24 min-h-screen flex flex-col items-center">
        <div className="w-full max-w-3xl mx-auto px-6 flex-1 flex flex-col">
          <Breadcrumb items={[{ label: 'Team Maturity Assessment' }]} />
          
          {!hasStarted && !showResults && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12 flex-1 flex flex-col justify-center mt-8"
            >
              <div className="w-16 h-16 bg-rule-3/20 rounded-full flex items-center justify-center mx-auto mb-6 text-rule-3">
                <BarChart2 size={32} />
              </div>
              <h1 className="text-4xl md:text-5xl font-serif text-ink mb-6">Team Maturity Assessment</h1>
              <p className="text-lg text-ink/70 max-w-2xl mx-auto mb-10 leading-relaxed">
                Understand your team's current dynamics across five critical dimensions: Communication, Trust, Accountability, Alignment, and Adaptability. This brief assessment provides a visual snapshot of your team's maturity level to help guide your development journey.
              </p>
              <div className="w-full max-w-xs mx-auto md:max-w-none md:w-auto">
                <Button size="lg" onClick={() => setHasStarted(true)} className="w-full md:w-auto">
                  Start Assessment <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          )}

          {hasStarted && !showResults && (
            <div className="flex-1 flex flex-col">
              <div className="mb-12">
                <div className="flex justify-between text-sm text-ink/70 font-medium mb-4 uppercase tracking-wider">
                  <span>Dimension {currentStep + 1} of {dimensions.length}</span>
                  <span>{Math.round(((currentStep) / dimensions.length) * 100)}% Completed</span>
                </div>
                <div className="h-2 w-full bg-ground-deep rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-rule-3"
                    initial={{ width: `${((currentStep) / dimensions.length) * 100}%` }}
                    animate={{ width: `${((currentStep + 1) / dimensions.length) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="flex-1 flex flex-col justify-center"
                >
                  <span className="text-rule-3 font-medium tracking-widest uppercase mb-4 text-sm">
                    {dimensions[currentStep].title}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-serif text-ink mb-10 leading-snug">
                    {dimensions[currentStep].question}
                  </h2>

                  <div className="space-y-3">
                    {options.map((opt) => {
                      const isSelected = answers[dimensions[currentStep].id] === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => handleSelect(opt.value)}
                          className={`w-full text-left px-5 py-4 md:px-6 md:py-5 rounded-xl border-2 transition-all duration-200 flex items-center justify-between min-h-[64px] ${
                            isSelected 
                              ? 'border-rule-3 bg-rule-3/10 text-ink' 
                              : 'border-ground-deep hover:border-rule-3/40 bg-white text-ink/70 hover:text-ink'
                          }`}
                        >
                          <span className="font-medium text-base md:text-lg pr-4">{opt.label}</span>
                          {isSelected && <CheckCircle className="text-rule-3 w-6 h-6 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="mt-8 md:mt-12 flex flex-col-reverse md:flex-row items-stretch md:items-center justify-between gap-4 pt-6 md:pt-8 border-t border-ground-deep">
                <Button 
                  variant="ghost" 
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                  className={`w-full md:w-auto ${currentStep === 0 ? 'opacity-0 pointer-events-none hidden md:inline-flex' : 'flex'}`}
                >
                  <ArrowLeft className="mr-2 w-4 h-4" /> Previous
                </Button>
                
                <Button 
                  onClick={handleNext}
                  disabled={!isCurrentAnswered}
                  className={`w-full md:w-auto ${currentStep === 0 ? 'md:ml-auto' : ''}`}
                >
                  {currentStep === dimensions.length - 1 ? 'View Results' : 'Next Question'} 
                  {currentStep !== dimensions.length - 1 && <ArrowRight className="ml-2 w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}

          {showResults && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex-1 flex flex-col items-center py-8"
            >
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-serif text-ink mb-4">Your Team Maturity Profile</h2>
                <p className="text-ink/70 max-w-xl mx-auto">
                  Based on your responses, here is a visual representation of your team's current development state across the five core dimensions.
                </p>
              </div>

              <div className="w-full max-w-2xl mb-6 flex justify-center">
                <div className="bg-ground-deep/30 p-1 rounded-lg flex space-x-1">
                  <button
                    onClick={() => setChartType('radar')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                      chartType === 'radar' ? 'bg-white text-ink shadow-sm' : 'text-ink/70 hover:text-ink'
                    }`}
                  >
                    Snapshot (Radar)
                  </button>
                  <button
                    onClick={() => setChartType('trend')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                      chartType === 'trend' ? 'bg-white text-ink shadow-sm' : 'text-ink/70 hover:text-ink'
                    }`}
                  >
                    View Trends
                  </button>
                </div>
              </div>

              <div className="w-full max-w-2xl mb-8 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="bg-ground-deep/30 p-1 rounded-lg flex space-x-1">
                  <button
                    onClick={() => setViewMode('individual')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                      viewMode === 'individual' ? 'bg-white text-ink shadow-sm' : 'text-ink/70 hover:text-ink'
                    }`}
                  >
                    Your Score
                  </button>
                  <button
                    onClick={() => setViewMode('team')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                      viewMode === 'team' ? 'bg-white text-ink shadow-sm' : 'text-ink/70 hover:text-ink'
                    }`}
                  >
                    Team Average
                  </button>
                </div>
                
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                  <div className="flex items-center space-x-2 bg-white px-2 py-1.5 rounded-lg border border-ground-deep shadow-sm w-full sm:w-auto justify-between">
                    <span className="text-xs font-medium text-ink/70 px-2">Grid:</span>
                    <div className="flex bg-ground-deep/30 rounded-md p-0.5">
                      <button 
                        onClick={() => setGridDensity('low')}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${gridDensity === 'low' ? 'bg-white text-ink shadow-sm' : 'text-ink/70 hover:text-ink'}`}
                      >
                        Low
                      </button>
                      <button 
                        onClick={() => setGridDensity('medium')}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${gridDensity === 'medium' ? 'bg-white text-ink shadow-sm' : 'text-ink/70 hover:text-ink'}`}
                      >
                        Med
                      </button>
                      <button 
                        onClick={() => setGridDensity('high')}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${gridDensity === 'high' ? 'bg-white text-ink shadow-sm' : 'text-ink/70 hover:text-ink'}`}
                      >
                        High
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 bg-white px-4 py-2 rounded-lg border border-ground-deep shadow-sm w-full sm:w-auto justify-between">
                    <span className="text-sm font-medium text-ink">Benchmarks</span>
                    <button 
                      onClick={() => setShowBenchmarks(!showBenchmarks)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${showBenchmarks ? 'bg-rule-3' : 'bg-ground-deep'}`}
                      aria-label="Toggle Benchmarks"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showBenchmarks ? 'translate-x-4' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-sm border border-ground-deep mb-12">
                <div className="h-[400px] w-full">
                  <Suspense fallback={<div className="h-full flex items-center justify-center text-ink/40 text-sm">Loading chart...</div>}>
                    <TeamMaturityChart
                      chartType={chartType}
                      calculateResults={calculateResults}
                      generateTrendData={generateTrendData}
                      dimensions={dimensions}
                      viewMode={viewMode}
                      showBenchmarks={showBenchmarks}
                      gridDensity={gridDensity}
                      setSelectedDimension={setSelectedDimension}
                    />
                  </Suspense>
                </div>
              </div>

              <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm border border-ground-deep mb-12 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-ground-deep/30 text-ink border-b border-ground-deep">
                        <th className="py-4 px-6 font-semibold text-sm">Dimension</th>
                        <th className="py-4 px-6 font-semibold text-sm text-center">Your Score</th>
                        <th className="py-4 px-6 font-semibold text-sm text-center">Team Average</th>
                        <th className="py-4 px-6 font-semibold text-sm text-center">Gap</th>
                        <th className="py-4 px-6 font-semibold text-sm text-center">Benchmark</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ground-deep/50">
                      {calculateResults().map((row, idx) => {
                        const gap = (row.individual - row.teamAverage).toFixed(1);
                        const isPositiveGap = Number(gap) > 0;
                        const isNegativeGap = Number(gap) < 0;
                        return (
                          <tr key={idx} className="hover:bg-ground-deep/10 transition-colors">
                            <td className="py-4 px-6 font-medium text-ink">{row.subject}</td>
                            <td className="py-4 px-6 text-center font-semibold text-rule-3">{row.individual.toFixed(1)}</td>
                            <td className="py-4 px-6 text-center text-ink/70">{row.teamAverage.toFixed(1)}</td>
                            <td className="py-4 px-6 text-center font-medium">
                              <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                                isPositiveGap ? 'bg-green-100 text-green-800' : isNegativeGap ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {isPositiveGap ? '+' : ''}{gap}
                              </span>
                            </td>
                            <td className="py-4 px-6 text-center text-ink/70 text-sm">{row.benchmark.toFixed(1)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm border border-ground-deep mb-12 overflow-hidden p-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                  <div>
                    <h3 className="text-xl font-serif text-ink mb-1">Team Alignment Heatmap</h3>
                    <p className="text-sm text-ink/70">Variance from Team Benchmark</p>
                  </div>
                  <div className="flex items-center flex-wrap gap-3 text-xs text-ink/70 font-medium bg-ground-deep/10 p-2 rounded-lg">
                    <div className="flex items-center space-x-1"><span className="w-3 h-3 rounded-sm bg-rose-500 shadow-sm"></span><span>-1.0+</span></div>
                    <div className="flex items-center space-x-1"><span className="w-3 h-3 rounded-sm bg-rose-200 shadow-sm"></span><span>-0.3+</span></div>
                    <div className="flex items-center space-x-1"><span className="w-3 h-3 rounded-sm bg-ground-deep/50 border border-ground-deep"></span><span>Aligned</span></div>
                    <div className="flex items-center space-x-1"><span className="w-3 h-3 rounded-sm bg-emerald-200 shadow-sm"></span><span>+0.3+</span></div>
                    <div className="flex items-center space-x-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 shadow-sm"></span><span>+1.0+</span></div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead>
                      <tr>
                        <th className="p-3 text-sm font-semibold text-ink border-b border-ground-deep/50 w-[20%]">Team Member</th>
                        {dimensions.map(d => (
                          <th key={d.id} className="p-3 text-xs font-semibold text-ink/70 border-b border-ground-deep/50 text-center w-[16%]">
                            {d.title}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {generateHeatmapData().map((row) => (
                        <tr key={row.member} className={row.member === 'You' ? 'bg-ground-deep/10' : ''}>
                          <td className="p-3 text-sm font-medium text-ink border-b border-ground-deep/30 flex items-center space-x-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] ${row.member === 'You' ? 'bg-ink text-white' : 'bg-ground-deep text-ink/70'}`}>
                              {row.member === 'You' ? 'Y' : row.member.split(' ')[1]}
                            </span>
                            <span>{row.member}</span>
                          </td>
                          {dimensions.map(d => {
                            const gap = row[d.id];
                            return (
                              <td key={d.id} className="p-1 border-b border-ground-deep/30">
                                <div 
                                  className={`w-full h-10 rounded-md flex items-center justify-center text-xs font-medium transition-all ${getHeatmapColor(gap)}`}
                                  title={`${row.member} gap on ${d.title}: ${gap > 0 ? '+' : ''}${gap.toFixed(1)}`}
                                >
                                  {gap > 0 ? '+' : ''}{gap.toFixed(1)}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 w-full max-w-4xl mb-12">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-ground-deep">
                  <h3 className="text-xl font-serif text-ink mb-3">Strengths</h3>
                  <p className="text-ink/70 text-sm leading-relaxed">
                    Look for the points reaching furthest outward on your chart. These represent the dimensions where your team exhibits the most mature behaviors. Leverage these strengths to pull up other areas.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-ground-deep">
                  <h3 className="text-xl font-serif text-ink mb-3">Growth Areas</h3>
                  <p className="text-ink/70 text-sm leading-relaxed">
                    The points closest to the center indicate dimensions needing attention. Targeted coaching, open dialogue, and structured team interventions in these areas will yield the highest return on team effectiveness.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <Button variant="outline" onClick={resetAssessment} className="w-full sm:w-auto">
                  Retake Assessment
                </Button>
                <Button onClick={() => window.location.href = '/team-effectiveness'} className="w-full sm:w-auto">
                  Explore Solutions
                </Button>
              </div>
            </motion.div>
          )}

        </div>
      </main>

      <AnimatePresence>
        {selectedDimension && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 10 }} 
              animate={{ opacity: 1, scale: 1, y: 0 }} 
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden relative"
            >
              <div className="flex justify-between items-center p-6 border-b border-ground-deep bg-ground-deep/10">
                <h3 className="text-xl font-serif text-ink pr-8">{selectedDimension.title}</h3>
                <button 
                  onClick={() => setSelectedDimension(null)} 
                  className="absolute top-6 right-6 text-ink/70 hover:text-ink transition-colors bg-white rounded-full p-1 shadow-sm border border-ground-deep"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-8">
                <div className="mb-6 pb-6 border-b border-ground-deep/50">
                  <h4 className="text-xs font-bold tracking-wider text-ink/70 uppercase mb-3">Diagnostic Insight</h4>
                  <p className="text-ink text-[15px] leading-relaxed">
                    {selectedDimension.insight}
                  </p>
                </div>
                <div>
                  <h4 className="text-xs font-bold tracking-wider text-ink/70 uppercase mb-4">Recommended Actions</h4>
                  <ul className="space-y-3">
                    {selectedDimension.actions.map((act: string, i: number) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-rule-3 mr-3 shrink-0 mt-0.5" />
                        <span className="text-sm text-ink/70 leading-relaxed">{act}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="p-6 border-t border-ground-deep bg-ground-deep/10 flex justify-end">
                <Button onClick={() => setSelectedDimension(null)}>
                  Close
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
